import { useState, useEffect, useRef } from 'react'
import { Video, VideoOff, Mic, MicOff, Loader2 } from 'lucide-react'
import { useMediaDevices } from '@/hooks'
import { DeviceSelector } from './DeviceSelector'

interface MeetingLobbyProps {
  roomId: string
  displayName: string
  onDisplayNameChange: (name: string) => void
  onJoin: (deviceIds: { audioDeviceId: string | null; videoDeviceId: string | null }) => void
  isJoining: boolean
}

export function MeetingLobby({
  roomId,
  displayName,
  onDisplayNameChange,
  onJoin,
  isJoining,
}: MeetingLobbyProps) {
  const [cameraEnabled, setCameraEnabled] = useState(true)
  const [micEnabled, setMicEnabled] = useState(true)
  const [showDevices, setShowDevices] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  const {
    audioInputDevices,
    audioOutputDevices,
    videoInputDevices,
    selectedAudioInputId,
    selectedAudioOutputId,
    selectedVideoInputId,
    selectAudioInput,
    selectAudioOutput,
    selectVideoInput,
    requestPermissions,
    stopStreams,
    audioPermission,
    videoPermission,
    audioStream,
    videoStream,
    isLoading,
    error,
  } = useMediaDevices()

  // Request permissions on mount
  useEffect(() => {
    requestPermissions()
  }, [])

  // Attach video stream to preview
  useEffect(() => {
    if (videoRef.current && videoStream && cameraEnabled) {
      videoRef.current.srcObject = videoStream
    } else if (videoRef.current && !cameraEnabled) {
      videoRef.current.srcObject = null
    }

    // Cleanup: release video element srcObject on unmount
    return () => {
      if (videoRef.current) {
        videoRef.current.srcObject = null
      }
    }
  }, [videoStream, cameraEnabled])

  const handleJoin = async () => {
    if (!displayName.trim()) {
      return
    }

    // Capture selected device IDs before stopping streams
    const deviceIds = {
      audioDeviceId: selectedAudioInputId,
      videoDeviceId: selectedVideoInputId,
    }

    // Stop preview streams before joining (LiveKit will create its own)
    stopStreams()

    // Wait for capture cards to release the device (important for Elgato etc.)
    await new Promise(resolve => setTimeout(resolve, 500))

    onJoin(deviceIds)
  }

  const permissionsGranted = audioPermission === 'granted' && videoPermission === 'granted'

  return (
    <div className="flex flex-col items-center justify-center h-full p-8">
      <div className="max-w-lg w-full space-y-6">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[var(--text-main)]">Ready to join?</h2>
          <p className="text-[var(--text-muted)] mt-1">Room: {roomId}</p>
        </div>

        {/* Video Preview */}
        <div className="relative aspect-video bg-[var(--bg-card)] rounded-xl overflow-hidden border border-[var(--border)]">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
            </div>
          ) : cameraEnabled && videoStream ? (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover transform -scale-x-100"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="w-24 h-24 rounded-full bg-[var(--primary)] flex items-center justify-center">
                <span className="text-4xl text-white font-bold">
                  {displayName ? displayName[0].toUpperCase() : '?'}
                </span>
              </div>
            </div>
          )}

          {/* Camera/Mic toggles overlay */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-3">
            <button
              onClick={() => setMicEnabled(!micEnabled)}
              className={`p-3 rounded-full transition-all ${
                micEnabled
                  ? 'bg-[var(--bg-element)] hover:bg-[var(--bg-hover)] text-[var(--text-main)]'
                  : 'bg-red-500 hover:bg-red-600 text-white'
              }`}
            >
              {micEnabled ? <Mic size={20} /> : <MicOff size={20} />}
            </button>
            <button
              onClick={() => setCameraEnabled(!cameraEnabled)}
              className={`p-3 rounded-full transition-all ${
                cameraEnabled
                  ? 'bg-[var(--bg-element)] hover:bg-[var(--bg-hover)] text-[var(--text-main)]'
                  : 'bg-red-500 hover:bg-red-600 text-white'
              }`}
            >
              {cameraEnabled ? <Video size={20} /> : <VideoOff size={20} />}
            </button>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Permission warning */}
        {!permissionsGranted && !isLoading && !error && (
          <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-yellow-400 text-sm">
            Please allow camera and microphone access to join the meeting.
          </div>
        )}

        {/* Name input */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-[var(--text-muted)]">Your name</label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => onDisplayNameChange(e.target.value)}
            placeholder="Enter your name"
            className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-3 rounded-lg focus:outline-none focus:border-[var(--primary)]"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleJoin()
            }}
          />
        </div>

        {/* Device settings toggle */}
        <button
          onClick={() => setShowDevices(!showDevices)}
          className="w-full text-left text-sm text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
        >
          {showDevices ? '▼ Hide device settings' : '▶ Show device settings'}
        </button>

        {/* Device selectors */}
        {showDevices && (
          <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4">
            <DeviceSelector
              audioInputDevices={audioInputDevices}
              audioOutputDevices={audioOutputDevices}
              videoInputDevices={videoInputDevices}
              selectedAudioInputId={selectedAudioInputId}
              selectedAudioOutputId={selectedAudioOutputId}
              selectedVideoInputId={selectedVideoInputId}
              onAudioInputChange={selectAudioInput}
              onAudioOutputChange={selectAudioOutput}
              onVideoInputChange={selectVideoInput}
            />
          </div>
        )}

        {/* Join button */}
        <button
          onClick={handleJoin}
          disabled={isJoining || !displayName.trim() || !permissionsGranted}
          className="w-full py-3 px-4 bg-[var(--primary)] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2"
        >
          {isJoining ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Joining...
            </>
          ) : (
            'Join Meeting'
          )}
        </button>
      </div>
    </div>
  )
}
